import React, { Fragment, useEffect } from "react"
import { X } from "lucide-react"
import { cn, randoms } from "lib/utils" 
import { parseContent } from "lib/render/parseContent"

import { DetailScreensRender, DisclaimerRender, LetterClosingRender, LetterPrefixRender, LogoAreaRender, NotesRender, PoliciesRender, StatementsRender, SummaryTableRender, SummaryBlockRender, SupportingImagesRender, TitleBlockRender, TopbarRender, TrackingRender, TrxHistoryTableRender } from "lib/renderers"

const Renderers:any = {
  compelling_evidence: StatementsRender,
  cs_notes: NotesRender,
  detail_screens: DetailScreensRender,
  disclaimer: DisclaimerRender,
  letter_closing: LetterClosingRender,
  letter_prefix: LetterPrefixRender,
  mm_logo: LogoAreaRender,
  opening_statements: StatementsRender,
  pkg_tracking: TrackingRender,
  policies: PoliciesRender,
  summary_table: SummaryTableRender,
  summary_block: SummaryBlockRender,
  supporting_images: SupportingImagesRender,
  topbar: TopbarRender,
  title_block: TitleBlockRender,
  trx_history: TrxHistoryTableRender,
}

const heightvals:any = {
  "xs": "h-8",
  "sm": "h-10",
  "md": "h-12",
  "lg": "h-16",
  "xl": "h-20",
  "2xl": "h-24"
}

function RenderComponent({
  refstr,
  renderPkg
}:{
  refstr:string 
  renderPkg?:any
}){
  const Component = Renderers[refstr] || null
  if (Component !== null) {
    return <Component key={refstr} renderPkg={renderPkg} elemtype={refstr} />
  } else {
    return <span>{ refstr }</span>
  }
}

function RenderStack({ 
  dir="h", 
  layout, 
  item, 
  mode, 
  editable,
  renderPkg,
  layoutBldrTools,
  handleWireframeClick
}:{ 
  dir:string; 
  layout:any; 
  item:any; 
  mode:string; 
  editable:boolean;
  renderPkg?:any;
  layoutBldrTools?:any
  handleWireframeClick?:any;
}){

  const { addToLayout, moveInLayout } = layoutBldrTools || {}

  // if( dir === "h" ){
  //   console.log(item.split) 
  // } 

  return (
    <div 
      onDragOver={(e:any) => e.preventDefault()}
      onDrop={(e:any) => {  
        e.preventDefault()
        e.stopPropagation()
        if( mode !== "build" ) return
        const type = e.dataTransfer.getData("type")
        addToLayout(type, item.seq)
      }}
      className={cn(
        mode === "build" && "border border-solid border-slate-700/30 gap-3",
        mode === "build" && dir === "h" && "min-h-12",
        mode === "build" && dir === "v" && "min-h-24",
      )} 
      style={{ 
        display: "flex",
        flexDirection: dir === "h" ? "row" : "column",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        width: dir === "h" ? `${item.split}%` : "100%" 
      }}
    >
      <RenderLayoutLevel 
        layout={layout} 
        level={item.level+1} 
        parent={item.refstr} 
        mode={mode} 
        editable={editable} 
        renderPkg={renderPkg}
        layoutBldrTools={layoutBldrTools}
        handleWireframeClick={handleWireframeClick}
      />
    </div>
  )
}

function RenderElement({ 
  elem, 
  mode="wireframe", 
  editable=false,
  renderPkg,
  handleWireframeClick,
  layoutBldrTools
}:{ 
  elem:any; 
  mode?:string;
  editable?:boolean;  
  renderPkg?:any;
  handleWireframeClick?:any;
  layoutBldrTools?:any
}){

  const { addToLayout, deleteFromLayout, moveInLayout } = layoutBldrTools || {}

  return (
    <div 
      onClick={() => (mode === "wireframe" || mode === "build") ? handleWireframeClick(elem.refstr) : undefined}
      draggable={ (mode === "build") ? true : false}
      onDragStart={(e:any) => {
        console.log(elem)
        e.dataTransfer.setData("type", `existing:${elem.refstr}`)
        e.dataTransfer.effectAllowed = "move"
      }}
      onDragOver={(e:any) => {
        e.preventDefault()
        console.log("dragover")
      }}
      onDrop={(e:any) => {
        e.preventDefault()
        e.stopPropagation()
        if( mode !== "build" ) return
        const type = e.dataTransfer.getData("type")

        if( type.startsWith("existing:") ){
          const refstr = type.split(":")[1]
          moveInLayout(refstr, elem.refstr)
        } else {
          addToLayout(type, elem.seq)
        }

        // addToLayout(e.dataTransfer.getData("type"), elem.seq)
      }}
      className={cn(
        (mode === "wireframe" || mode === "build") ? "cursor-pointer border border-solid border-slate-700" : "",
        (mode === "wireframe" || mode === "build") && heightvals[elem.height],
      )}
      style={{
        width: "100%",
        display: "flex",
        position: "relative",
      }}
    >
      { (mode === "wireframe" || mode === "build") ? (
        <>
          <span className="absolute top-[2px] left-[4px] text-[.7rem] text-gray-50 min-h-8">{ elem.refstr }</span>
          <X 
            className="absolute top-[6px] right-[4px] text-gray-50 w-3 h-3 cursor-pointer" 
            onClick={(e:any) => {
              e.stopPropagation();
              deleteFromLayout(elem.refstr) } 
            } />
        </>
      ):(
        <RenderComponent key={elem.refstr} refstr={elem.refstr} renderPkg={renderPkg} />
      )}
    </div>
  )
}

function RenderLayoutLevel({ 
  layout,
  level, 
  parent, 
  mode, 
  editable,
  renderPkg,
  layoutBldrTools,
  handleWireframeClick
}:{ 
  layout:any;
  level:number; 
  parent:string|null;
  mode:string; 
  editable:boolean; 
  renderPkg?:any;
  layoutBldrTools?:any;
  handleWireframeClick?:any;
}){

  const stackProps = {
    layout,
    mode,
    editable,
    renderPkg,
    handleWireframeClick
  }


  return layout?.filter( (item:any) => item.level === level && item.parent === parent )
  .sort( (a:any, b:any ) => a.seq - b.seq )
  .map( (item:any, idx:number) => (
    <Fragment key={idx}>
      { item.type === "hstack" && <RenderStack dir="h" item={item} layoutBldrTools={layoutBldrTools} {...stackProps} /> }
      { item.type === "vstack" && <RenderStack dir="v" item={item} layoutBldrTools={layoutBldrTools} {...stackProps} /> }
      { item.type === "element" && (
        <RenderElement 
          elem={item} 
          mode={mode} 
          editable={editable} 
          renderPkg={renderPkg} 
          layoutBldrTools={layoutBldrTools}
          handleWireframeClick={handleWireframeClick} 
        />
      )}
    </Fragment>
  ))
}

export default function RenderLayout({ 
  layout, 
  editable=false, 
  mode,
  renderPkg,
  layoutBldrTools,
  handleWireframeClick
}:{ 
  layout:any; 
  editable?:boolean; 
  mode:string;
  renderPkg?:any;
  layoutBldrTools?:any;
  handleWireframeClick?:any;
}){

  // useEffect(() => {
  //   console.log(layout)
  // },[layout])

  return (
    <div 
      id="mm-render-layout" 
      className="gap-2"
      style={{ width: "100%", display: "flex", flexDirection: "column" }}
      onDrop={(e:any) => {
        console.log("drop")
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      <RenderLayoutLevel 
        layout={layout} 
        level={1} 
        parent={null} 
        mode={mode} 
        renderPkg={renderPkg}
        editable={editable} 
        layoutBldrTools={layoutBldrTools}
        handleWireframeClick={handleWireframeClick}
      />
    </div>
  )
}